import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { appointmentsAPI } from "@/lib/appointments-api";
import { queryKeys, invalidateQueries } from "@/lib/query-keys";
import type {
  CreateDiseaseHistoryRequest,
  UpdateDiseaseHistoryRequest,
  CreateFamilyDiseaseHistoryRequest,
  UpdateFamilyDiseaseHistoryRequest,
  CreatePatientLifestyleRequest,
  UpdatePatientLifestyleRequest,
  CreatePatientActivityRequest,
  UpdatePatientActivityRequest,
  CreatePatientSurgeryRequest,
  DiseaseListParams,
  DiseaseSource,
  DiseaseAppointmentSource,
  LifestyleTypeListParams,
  ActivityTypeListParams,
  CreatePatientSymptomRequest,
  SymptomListParams,
  CreateBloodPressureRequest,
  CreateBloodGlucoseRequest,
  CreateHeartRateRequest,
  CreateTemperatureRequest,
  CreateRespiratoryRateRequest,
  CreateOxygenSaturationRequest,
  CreatePatientAllergyRequest,
  ResolveCustomDiseaseRequest,
  PatientDiseaseHistory,
} from "@/types/appointments";

// 1-hour stale time for catalog data
const CATALOG_STALE_TIME = 60 * 60 * 1000;

// Catalog hooks with 1-hour caching
export const useDiseases = (params?: DiseaseListParams) => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.diseases(params as any),
    queryFn: () => appointmentsAPI.getDiseases(params),
    staleTime: CATALOG_STALE_TIME,
    select: (data) => data || [],
  });
};

export const useLifestyleTypes = (params?: LifestyleTypeListParams) => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.lifestyleTypes(params as any),
    queryFn: () => appointmentsAPI.getLifestyleTypes(params),
    staleTime: CATALOG_STALE_TIME,
    select: (data) => data || [],
  });
};

export const useLifestyleFrequencies = () => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.lifestyleFrequencies(),
    queryFn: () => appointmentsAPI.getLifestyleFrequencies(),
    staleTime: CATALOG_STALE_TIME,
    select: (data) => data || [],
  });
};

export const useActivityTypes = (params?: ActivityTypeListParams) => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.activityTypes(params as any),
    queryFn: () => appointmentsAPI.getActivityTypes(params),
    staleTime: CATALOG_STALE_TIME,
    select: (data) => data || [],
  });
};

export const useActivityDurations = () => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.activityDurations(),
    queryFn: () => appointmentsAPI.getActivityDurations(),
    staleTime: CATALOG_STALE_TIME,
    select: (data) => data || [],
  });
};

export const useActivityFrequencies = () => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.activityFrequencies(),
    queryFn: () => appointmentsAPI.getActivityFrequencies(),
    staleTime: CATALOG_STALE_TIME,
    select: (data) => data || [],
  });
};

export const useSurgeries = () => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.surgeries(),
    queryFn: () => appointmentsAPI.getSurgeries(),
    staleTime: CATALOG_STALE_TIME,
    select: (data) => data || [],
  });
};

// Client-side search utilities
export const useDiseaseSearch = (
  searchTerm: string,
  params?: DiseaseListParams
) => {
  const { data: diseases = [] } = useDiseases(params);

  const filteredDiseases =
    searchTerm.length >= 2
      ? diseases.filter(
          (disease) =>
            disease.display_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            disease.long_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            disease.short_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            (disease.other_names &&
              disease.other_names
                .toLowerCase()
                .includes(searchTerm.toLowerCase()))
        )
      : diseases;

  return { diseases: filteredDiseases };
};

export const useLifestyleTypeSearch = (
  searchTerm: string,
  params?: LifestyleTypeListParams
) => {
  const { data: lifestyleTypes = [] } = useLifestyleTypes(params);

  const filteredTypes =
    searchTerm.length >= 2
      ? lifestyleTypes.filter(
          (type) =>
            type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (type.other_names &&
              type.other_names.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      : lifestyleTypes;

  return { lifestyleTypes: filteredTypes };
};

export const useActivityTypeSearch = (
  searchTerm: string,
  params?: ActivityTypeListParams
) => {
  const { data: activityTypes = [] } = useActivityTypes(params);

  const filteredTypes =
    searchTerm.length >= 2
      ? activityTypes.filter(
          (type) =>
            type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (type.other_names &&
              type.other_names.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      : activityTypes;

  return { activityTypes: filteredTypes };
};

export const useSurgerySearch = (searchTerm: string) => {
  const { data: surgeries = [] } = useSurgeries();

  const filteredSurgeries =
    searchTerm.length >= 2
      ? surgeries.filter(
          (surgery) =>
            surgery.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (surgery.other_names &&
              surgery.other_names
                .toLowerCase()
                .includes(searchTerm.toLowerCase()))
        )
      : surgeries;

  return { surgeries: filteredSurgeries };
};

// AppointmentDiseaseDraftItemSerializer (and PatientDiseaseSerializer) emit the
// catalog row as `disease_details` while keeping `disease` as a write-only UUID.
// Consumers across the app read `h.disease.display_name`, matching the inflated
// shape returned by `disease-summary`. Normalize here so the rest of the FE can
// treat both endpoints uniformly without duplicating the mapping.
const normalizeDiseaseHistoryRow = (
  row: PatientDiseaseHistory & { disease_details?: unknown }
): PatientDiseaseHistory => {
  if (row.disease && typeof row.disease === "object") return row;
  const details = row.disease_details;
  if (details && typeof details === "object") {
    return { ...row, disease: details as PatientDiseaseHistory["disease"] };
  }
  return { ...row, disease: null };
};

// Data fetching hooks for Consultation page.
// `id` semantics depend on `source`:
//   - intake_form (or omitted) → patientId (committed PatientDisease)
//   - prescription | meal_plan → appointmentId (AppointmentDiseaseDraftItem)
export const usePatientDiseaseHistory = (
  id: string,
  source?: DiseaseSource
) => {
  return useQuery({
    queryKey: queryKeys.consultation.patientData.diseaseHistory(id, source),
    queryFn: () => appointmentsAPI.getPatientDiseaseHistory(id, source),
    select: (data) => (data ?? []).map(normalizeDiseaseHistoryRow),
    enabled: !!id,
  });
};

// Initialise the appointment disease draft. Call before the first read of
// prescription/meal-plan disease data so the draft exists server-side.
//
// The same endpoint also initialises AppointmentSymptomsDraft from committed
// PatientSymptoms (see patient/views/disease_management.py), so we invalidate
// the symptom queries here too — otherwise the symptoms tab would render an
// empty list until the next manual refetch.
export const useInitializeAppointmentDiseaseDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      source,
    }: {
      appointmentId: string;
      source: DiseaseAppointmentSource;
    }) =>
      appointmentsAPI.initializeAppointmentDiseaseDraft(appointmentId, source),
    onSuccess: (_, { appointmentId, source }) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientDiseaseHistory(appointmentId, source)
      );
      queryClient.invalidateQueries({
        queryKey: ["patientSymptoms", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["appointment-symptoms", appointmentId],
      });
    },
  });
};

export const useDiseaseSummary = (patientId: string) => {
  return useQuery({
    queryKey: queryKeys.consultation.patientData.diseaseSummary(patientId),
    queryFn: () => appointmentsAPI.getDiseaseSummary(patientId),
    staleTime: 30_000,
    enabled: !!patientId,
  });
};

// Bust every cached `diseaseHistory` query that targets this appointment,
// regardless of `source` ("prescription" | "meal_plan"), so all surfaces
// reading the appointment draft refetch after a mutation.
const invalidateAppointmentDraftHistory = (
  queryClient: QueryClient,
  appointmentId: string
) =>
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key.includes("diseaseHistory") &&
        key.includes(appointmentId)
      );
    },
  });

export const useConfirmDiseaseHistory = (
  appointmentId: string,
  patientId: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftItemId: string) =>
      appointmentsAPI.confirmDiseaseHistory(appointmentId, draftItemId),
    onSuccess: () => {
      invalidateAppointmentDraftHistory(queryClient, appointmentId);
      queryClient.invalidateQueries(
        invalidateQueries.diseaseSummary(patientId)
      );
    },
  });
};

export const useRejectDiseaseHistory = (
  appointmentId: string,
  patientId: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftItemId: string) =>
      appointmentsAPI.rejectDiseaseHistory(appointmentId, draftItemId),
    onSuccess: () => {
      invalidateAppointmentDraftHistory(queryClient, appointmentId);
      queryClient.invalidateQueries(
        invalidateQueries.diseaseSummary(patientId)
      );
    },
  });
};

export const useDeactivateDiseaseHistory = (
  appointmentId: string,
  patientId: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftItemId: string) =>
      appointmentsAPI.deactivateDiseaseHistory(appointmentId, draftItemId),
    onSuccess: () => {
      invalidateAppointmentDraftHistory(queryClient, appointmentId);
      queryClient.invalidateQueries(
        invalidateQueries.diseaseSummary(patientId)
      );
    },
  });
};

// For prescription past rows; per
// docs/frontend-disease-api-flow.md (Update Draft Item Fields > Notes,
// Quadrant moves table), old prescription rows must use this endpoint
// instead of PATCH/DELETE for removal. Not valid for meal_plan source.
export const useExcludeFromPrescription = (appointmentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftItemId: string) =>
      appointmentsAPI.excludeFromPrescription(appointmentId, draftItemId),
    onSuccess: () => {
      invalidateAppointmentDraftHistory(queryClient, appointmentId);
    },
  });
};

// Symmetric restore for old prescription rows; PATCH activity_status:"active"
// is rejected by BE for past prescription rows by design, so
// this is the only path to put an excluded row back into the prescription.
export const useIncludeInPrescription = (appointmentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftItemId: string) =>
      appointmentsAPI.includeInPrescription(appointmentId, draftItemId),
    onSuccess: () => {
      invalidateAppointmentDraftHistory(queryClient, appointmentId);
    },
  });
};

export const useResolveCustomDisease = (
  appointmentId: string,
  patientId: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      draftItemId,
      data,
    }: {
      draftItemId: string;
      data: ResolveCustomDiseaseRequest;
    }) =>
      appointmentsAPI.resolveCustomDisease(appointmentId, draftItemId, data),
    onSuccess: () => {
      invalidateAppointmentDraftHistory(queryClient, appointmentId);
      queryClient.invalidateQueries(
        invalidateQueries.diseaseSummary(patientId)
      );
    },
  });
};

export const useRejectCustomDisease = (
  appointmentId: string,
  patientId: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftItemId: string) =>
      appointmentsAPI.rejectCustomDisease(appointmentId, draftItemId),
    onSuccess: () => {
      invalidateAppointmentDraftHistory(queryClient, appointmentId);
      queryClient.invalidateQueries(
        invalidateQueries.diseaseSummary(patientId)
      );
    },
  });
};

export const usePatientFamilyDiseaseHistory = (patientId: string) => {
  return useQuery({
    queryKey:
      queryKeys.consultation.patientData.familyDiseaseHistory(patientId),
    queryFn: () => appointmentsAPI.getPatientFamilyDiseaseHistory(patientId),
    select: (data) => data || [],
    enabled: !!patientId,
  });
};

export const usePatientLifestyle = (appointmentId: string) => {
  return useQuery({
    queryKey: queryKeys.consultation.patientData.lifestyle(appointmentId),
    queryFn: () => appointmentsAPI.getPatientLifestyle(appointmentId),
    select: (data) => data || [],
    enabled: !!appointmentId,
  });
};

export const usePatientActivity = (appointmentId: string) => {
  return useQuery({
    queryKey: queryKeys.consultation.patientData.activity(appointmentId),
    queryFn: () => appointmentsAPI.getPatientActivity(appointmentId),
    select: (data) => data || [],
    enabled: !!appointmentId,
  });
};

export const usePatientSurgeries = (appointmentId: string) => {
  return useQuery({
    queryKey: queryKeys.consultation.patientData.surgeries(appointmentId),
    queryFn: () => appointmentsAPI.getPatientSurgeries(appointmentId),
    select: (data) => data || [],
    enabled: !!appointmentId,
  });
};

export const usePatientConcerns = (appointmentId: string) => {
  return useQuery({
    queryKey: ["patientConcerns", appointmentId],
    queryFn: () => appointmentsAPI.getPatientConcerns(appointmentId),
    select: (data) => data || [],
    enabled: !!appointmentId,
  });
};

export const useCreatePatientConcerns = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: { concerns: string[]; is_active?: boolean };
    }) => appointmentsAPI.createPatientConcerns(appointmentId, data),
    onSuccess: () => {
      // Invalidate concerns - we need patientId, but we can invalidate all concerns queries
      queryClient.invalidateQueries({ queryKey: ["patientConcerns"] });
    },
  });
};

export const useUpdatePatientConcerns = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: { concerns?: string[]; is_active?: boolean };
    }) => appointmentsAPI.updatePatientConcerns(appointmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patientConcerns"] });
    },
  });
};

export const usePatientInfo = (patientId: string, appointmentId: string) => {
  return useQuery({
    queryKey: ["patientInfo", patientId, appointmentId],
    queryFn: () => appointmentsAPI.getPatientInfo(patientId, appointmentId),
    select: (data) => data.data,
    enabled: !!patientId && !!appointmentId,
  });
};

export const useAppointmentHistory = (
  patientId: string,
  status: "completed" = "completed"
) => {
  return useQuery({
    queryKey: queryKeys.consultation.patientData.appointmentHistory(
      patientId,
      status
    ),
    queryFn: () => appointmentsAPI.getAppointmentHistory(patientId, status),
    select: (data) => data || [],
    enabled: !!patientId,
  });
};

// Mutation hooks for CRUD operations.
// `id` is patientId for source=intake_form, appointmentId for prescription/meal_plan.
export const useCreateDiseaseHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: CreateDiseaseHistoryRequest;
    }) => appointmentsAPI.createPatientDiseaseHistory(id, data),
    onSuccess: (_, { id, data }) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientDiseaseHistory(id, data.source)
      );
      // Also bust any disease-summary queries — the patient surface in
      // PatientOverview reads from there and we don't always know patientId here.
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.includes("diseaseSummary"),
      });
    },
  });
};

export const useUpdateDiseaseHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      rowId,
      data,
      source,
    }: {
      id: string;
      rowId: string;
      data: UpdateDiseaseHistoryRequest;
      source?: DiseaseSource;
    }) => appointmentsAPI.updatePatientDiseaseHistory(id, rowId, data, source),
    onSuccess: (_, { id, source }) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientDiseaseHistory(id, source)
      );
    },
  });
};

export const useDeleteDiseaseHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      rowId,
      source,
    }: {
      id: string;
      rowId: string;
      source?: DiseaseSource;
    }) => appointmentsAPI.deletePatientDiseaseHistory(id, rowId, source),
    onSuccess: (_, { id }) => {
      // Invalidate all disease history queries for this id (with or without source).
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key.includes("diseaseHistory") &&
            key.includes(id)
          );
        },
      });
    },
  });
};

export const useCreateFamilyDiseaseHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFamilyDiseaseHistoryRequest) =>
      appointmentsAPI.createFamilyDiseaseHistory(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientFamilyDiseaseHistory(response.patient)
      );
    },
  });
};

export const useUpdateFamilyDiseaseHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      historyId,
      data,
    }: {
      historyId: string;
      data: UpdateFamilyDiseaseHistoryRequest;
    }) => appointmentsAPI.updateFamilyDiseaseHistory(historyId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientFamilyDiseaseHistory(response.patient)
      );
    },
  });
};

export const useDeleteFamilyDiseaseHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ historyId }: { historyId: string; patientId: string }) =>
      appointmentsAPI.deleteFamilyDiseaseHistory(historyId),
    onSuccess: (_, { patientId }) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientFamilyDiseaseHistory(patientId)
      );
    },
  });
};

export const useCreatePatientLifestyle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      patientId,
      data,
    }: {
      patientId: string;
      data: CreatePatientLifestyleRequest;
    }) => appointmentsAPI.createPatientLifestyle(patientId, data),
    onSuccess: (_, { patientId }) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientLifestyle(patientId)
      );
    },
  });
};

export const useUpdatePatientLifestyle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      patientId,
      lifestyleId,
      data,
    }: {
      patientId: string;
      lifestyleId: string;
      data: UpdatePatientLifestyleRequest;
    }) => appointmentsAPI.updatePatientLifestyle(patientId, lifestyleId, data),
    onSuccess: (_, { patientId }) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientLifestyle(patientId)
      );
    },
  });
};

export const useDeletePatientLifestyle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      patientId,
      lifestyleId,
    }: {
      patientId: string;
      lifestyleId: string;
    }) => appointmentsAPI.deletePatientLifestyle(patientId, lifestyleId),
    onSuccess: (_, { patientId }) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientLifestyle(patientId)
      );
    },
  });
};

export const useCreatePatientActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: CreatePatientActivityRequest;
    }) => appointmentsAPI.createPatientActivity(appointmentId, data),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientActivity(appointmentId)
      );
    },
  });
};

export const useUpdatePatientActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      activityId,
      data,
    }: {
      appointmentId: string;
      activityId: string;
      data: UpdatePatientActivityRequest;
    }) =>
      appointmentsAPI.updatePatientActivity(appointmentId, activityId, data),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientActivity(appointmentId)
      );
    },
  });
};

export const useDeletePatientActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      activityId,
    }: {
      appointmentId: string;
      activityId: string;
    }) => appointmentsAPI.deletePatientActivity(appointmentId, activityId),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientActivity(appointmentId)
      );
    },
  });
};

export const useCreatePatientSurgery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: CreatePatientSurgeryRequest;
    }) => appointmentsAPI.createPatientSurgery(appointmentId, data),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientSurgeries(appointmentId)
      );
    },
  });
};

export const useDeletePatientSurgery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      surgeryId,
    }: {
      appointmentId: string;
      surgeryId: string;
    }) => appointmentsAPI.deletePatientSurgery(appointmentId, surgeryId),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientSurgeries(appointmentId)
      );
    },
  });
};

// Symptoms Hooks
export const usePatientSymptoms = (appointmentId: string) => {
  return useQuery({
    queryKey: ["patientSymptoms", appointmentId],
    queryFn: () => appointmentsAPI.getPatientSymptoms(appointmentId),
    select: (data) => data || [],
    enabled: !!appointmentId,
    staleTime: 0,
    refetchOnMount: "always",
  });
};

export const useCreatePatientSymptom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePatientSymptomRequest) =>
      appointmentsAPI.createPatientSymptom(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["patientSymptoms", variables.appointment],
      });
      // Nutrition profile reads the same endpoint under a different key.
      queryClient.invalidateQueries({
        queryKey: ["appointment-symptoms", variables.appointment],
      });
    },
  });
};

export const useDeletePatientSymptom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ symptomId }: { symptomId: string; appointmentId: string }) =>
      appointmentsAPI.deletePatientSymptom(symptomId),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ["patientSymptoms", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["appointment-symptoms", appointmentId],
      });
    },
  });
};

export const useSymptomSearch = (
  searchTerm: string,
  params?: SymptomListParams
) => {
  return useQuery({
    queryKey: ["symptoms", searchTerm, params],
    queryFn: () =>
      appointmentsAPI.getSymptoms({ ...params, search: searchTerm }),
    select: (data) => data || [],
    enabled: searchTerm.length >= 2,
  });
};

// Vitals Hooks
export const useVitalsHome = (appointmentId: string) => {
  return useQuery({
    queryKey: ["vitalsHome", appointmentId],
    queryFn: () => appointmentsAPI.getVitalsHome(appointmentId),
    enabled: !!appointmentId,
  });
};

export const useCreateBloodPressure = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: CreateBloodPressureRequest;
    }) => appointmentsAPI.createBloodPressure(appointmentId, data),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ["vitalsHome", appointmentId],
      });
    },
  });
};

export const useCreateBloodGlucose = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: CreateBloodGlucoseRequest;
    }) => appointmentsAPI.createBloodGlucose(appointmentId, data),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ["vitalsHome", appointmentId],
      });
    },
  });
};

export const useCreateHeartRate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: CreateHeartRateRequest;
    }) => appointmentsAPI.createHeartRate(appointmentId, data),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ["vitalsHome", appointmentId],
      });
    },
  });
};

export const useCreateTemperature = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: CreateTemperatureRequest;
    }) => appointmentsAPI.createTemperature(appointmentId, data),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ["vitalsHome", appointmentId],
      });
    },
  });
};

export const useCreateRespiratoryRate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: CreateRespiratoryRateRequest;
    }) => appointmentsAPI.createRespiratoryRate(appointmentId, data),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ["vitalsHome", appointmentId],
      });
    },
  });
};

export const useCreateOxygenSaturation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: CreateOxygenSaturationRequest;
    }) => appointmentsAPI.createOxygenSaturation(appointmentId, data),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ["vitalsHome", appointmentId],
      });
    },
  });
};

// Vitals Helper Hooks
export const useBloodPressurePositions = () => {
  return useQuery({
    queryKey: ["bloodPressurePositions"],
    queryFn: () => appointmentsAPI.getBloodPressurePositions(),
    staleTime: CATALOG_STALE_TIME,
  });
};

export const useBloodPressureLocations = () => {
  return useQuery({
    queryKey: ["bloodPressureLocations"],
    queryFn: () => appointmentsAPI.getBloodPressureLocations(),
    staleTime: CATALOG_STALE_TIME,
  });
};

export const useTemperatureLocations = () => {
  return useQuery({
    queryKey: ["temperatureLocations"],
    queryFn: () => appointmentsAPI.getTemperatureLocations(),
    staleTime: CATALOG_STALE_TIME,
  });
};

export const useBloodGlucoseSampleSources = () => {
  return useQuery({
    queryKey: ["bloodGlucoseSampleSources"],
    queryFn: () => appointmentsAPI.getBloodGlucoseSampleSources(),
    staleTime: CATALOG_STALE_TIME,
  });
};

export const useBloodGlucoseSampleTypes = () => {
  return useQuery({
    queryKey: ["bloodGlucoseSampleTypes"],
    queryFn: () => appointmentsAPI.getBloodGlucoseSampleTypes(),
    staleTime: CATALOG_STALE_TIME,
  });
};

// Medicines & Allergies Hooks
export const usePatientAllergies = (appointmentId: string) => {
  return useQuery({
    queryKey: ["patientAllergies", appointmentId],
    queryFn: () => appointmentsAPI.getPatientAllergies(appointmentId),
    enabled: !!appointmentId,
    select: (data) => data || [],
  });
};

export const useAddPatientAllergy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: CreatePatientAllergyRequest;
    }) => appointmentsAPI.addPatientAllergy(appointmentId, data),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ["patientAllergies", appointmentId],
      });
    },
  });
};

export const useAllergies = (search: string) => {
  return useQuery({
    queryKey: ["allergies", search],
    queryFn: () => appointmentsAPI.getAllergies(search),
    enabled: search.length >= 1,
    select: (data) => data || [],
  });
};

export const useAllergyTypes = () => {
  return useQuery({
    queryKey: ["allergyTypes"],
    queryFn: () => appointmentsAPI.getAllergyTypes(),
    staleTime: CATALOG_STALE_TIME,
    select: (data) => data || [],
  });
};

export const useDeletePatientAllergy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      allergyId,
    }: {
      appointmentId: string;
      allergyId: string;
    }) => appointmentsAPI.deletePatientAllergy(appointmentId, allergyId),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ["patientAllergies", appointmentId],
      });
    },
  });
};

export const usePatientMedicines = (appointmentId: string) => {
  return useQuery({
    queryKey: ["patientMedicines", appointmentId],
    queryFn: () => appointmentsAPI.getPatientMedicines(appointmentId),
    enabled: !!appointmentId,
  });
};

export const usePatientDocuments = (
  patientId: string,
  consolidated: boolean = false
) => {
  return useQuery({
    queryKey: ["patientDocuments", patientId, consolidated],
    queryFn: () => appointmentsAPI.getPatientDocuments(patientId, consolidated),
    enabled: !!patientId,
    select: (data) => data || [],
  });
};

export const usePastPrescriptions = (appointmentId: string) => {
  return useQuery({
    queryKey: ["pastPrescriptions", appointmentId],
    queryFn: () => appointmentsAPI.getPastPrescriptions(appointmentId),
    enabled: !!appointmentId,
    select: (data) => data || [],
  });
};
